import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { Check, ChevronUp, Undo2 } from 'lucide-react';
import { useState } from 'react';
import {
  activeCountClasses,
  latestChangeUndoClasses,
  ledgerCountClasses,
  ledgerShellClasses,
  ledgerTaskLinkClasses,
  ledgerToggleClasses,
  ledgerUndoClasses,
} from './noteInstructions.styles';
import type {
  ChangeLedgerProps,
  ChangeLedgerRowProps,
  ChangeTarget,
  TargetLabelsProps,
  UndoButtonProps,
} from './noteInstructions.types';

const TARGET_LABELS: Record<ChangeTarget, string> = {
  note: 'Nóta',
  new_task: 'Nýtt verkefni',
  task_updated: 'Verkefni uppfært',
};

const NOT_UNDOABLE = 'Þessu hefur verið breytt síðan og ekki er hægt að afturkalla það.';

const formatTime = (date: Date) => date.toLocaleTimeString('is-IS', { hour: '2-digit', minute: '2-digit' });

/** Target labels; task targets link to the Verkefni tab when the caller can switch to it. */
const TargetLabels = ({ targets, onShowTasks }: TargetLabelsProps) => (
  <>
    {Array.from(new Set(targets)).map((target, index) => (
      <span key={target}>
        {index > 0 && ' · '}
        {target !== 'note' && onShowTasks ? (
          <Button variant="link" size="sm" className={ledgerTaskLinkClasses} onClick={onShowTasks}>
            {TARGET_LABELS[target]}
          </Button>
        ) : (
          TARGET_LABELS[target]
        )}
      </span>
    ))}
  </>
);

/** Undo for one change; a disabled button gets no pointer events, so the reason sits on its wrapper. */
const UndoButton = ({ entry, disabled, isUndoable, onUndo, inline }: UndoButtonProps) => (
  <span title={isUndoable ? undefined : NOT_UNDOABLE} className="shrink-0">
    <Button
      size="sm"
      variant={inline ? 'ghost' : 'outline'}
      className={inline ? latestChangeUndoClasses : ledgerUndoClasses}
      disabled={disabled || !isUndoable}
      onClick={onUndo}
      aria-label={`Afturkalla: ${entry.summary}`}
    >
      <Undo2 className="h-3 w-3" />
      Afturkalla
    </Button>
  </span>
);

const ChangeLedgerRow = ({ entry, disabled, isUndoable, onUndo, onShowTasks }: ChangeLedgerRowProps) => (
  <div className="flex min-w-0 items-center gap-2 text-xs">
    <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', entry.applied ? 'bg-green-600' : 'bg-slate-300')} />
    <span className={cn('shrink-0 font-medium text-slate-800', !entry.applied && 'opacity-50')}>
      <TargetLabels targets={entry.targets} onShowTasks={entry.applied ? onShowTasks : undefined} />
    </span>
    <span
      className={cn('min-w-0 truncate text-slate-500', !entry.applied && 'line-through opacity-50')}
      title={entry.summary}
    >
      {entry.summary}
    </span>
    <span className="ml-auto shrink-0 font-mono text-[11px] text-slate-400">
      {entry.applied ? formatTime(entry.appliedAt) : 'Afturkallað'}
    </span>
    {entry.applied && <UndoButton entry={entry} disabled={disabled} isUndoable={isUndoable} onUndo={onUndo} />}
  </div>
);

/** The latest change: what it did, a link to touched tasks, and its undo. */
const LatestChangeRow = ({ entry, disabled, isUndoable, onUndo, onShowTasks }: ChangeLedgerRowProps) => {
  const touchedTasks = entry.applied && onShowTasks && entry.targets.some((target) => target !== 'note');
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2 text-xs">
      {entry.applied ? (
        <Check className="h-3.5 w-3.5 shrink-0 text-green-700" aria-hidden />
      ) : (
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300" />
      )}
      <span
        className={cn('min-w-0 truncate text-slate-700', !entry.applied && 'line-through opacity-50')}
        title={entry.summary}
      >
        {entry.summary}
      </span>
      {touchedTasks && (
        <Button variant="link" size="sm" className={cn(ledgerTaskLinkClasses, 'shrink-0')} onClick={onShowTasks}>
          Verkefni
        </Button>
      )}
      <span className="ml-auto shrink-0">
        {entry.applied ? (
          <UndoButton entry={entry} disabled={disabled} isUndoable={isUndoable} onUndo={onUndo} inline />
        ) : (
          <span className="text-[11px] text-slate-400">Afturkallað</span>
        )}
      </span>
    </div>
  );
};

/**
 * "Breytingar": one green row with the latest change and its count; the count expands upward
 * to the session's full list. Each change can be undone on its own while what it touched is unchanged.
 */
export const ChangeLedger = ({ entries, disabled, isUndoable, onUndo, onShowTasks }: ChangeLedgerProps) => {
  const [open, setOpen] = useState(false);
  if (entries.length === 0) return null;
  const latestIndex = entries.length - 1;
  const appliedCount = entries.filter((entry) => entry.applied).length;
  // Derived, not synced: closed whenever there is nothing older (e.g. after a history reset).
  const expandable = entries.length > 1;
  const rowProps = (index: number) => ({
    entry: entries[index],
    disabled,
    isUndoable: isUndoable(entries[index].id),
    onUndo: () => onUndo(entries[index].id),
    onShowTasks,
  });

  return (
    <Collapsible open={open && expandable} onOpenChange={setOpen} className={ledgerShellClasses}>
      <CollapsibleContent>
        <ul className="max-h-40 space-y-1 overflow-y-auto border-slate-100 border-b px-3 py-2">
          {entries.map((entry, index) =>
            index === latestIndex ? null : (
              <li key={entry.id}>
                <ChangeLedgerRow {...rowProps(index)} />
              </li>
            )
          )}
        </ul>
      </CollapsibleContent>
      <div className="flex items-center gap-1 py-1 pr-1 pl-3">
        <LatestChangeRow {...rowProps(latestIndex)} />
        <CollapsibleTrigger
          disabled={!expandable}
          className={ledgerToggleClasses}
          aria-label={expandable ? `Breytingar (${appliedCount}): sýna allar` : `Breytingar (${appliedCount})`}
        >
          <span
            className={cn(ledgerCountClasses, appliedCount > 0 ? activeCountClasses : 'bg-slate-100 text-slate-500')}
          >
            {appliedCount}
          </span>
          {expandable && <ChevronUp className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />}
        </CollapsibleTrigger>
      </div>
    </Collapsible>
  );
};
